import { create } from 'zustand';
import type { PricingType } from '@/theme/uboTheme';
import type { InvestmentCurrency, InvestmentLabelLanguage } from '@/types';

/**
 * Inspector-state voor het investeringsblok.
 *
 * De node views van het investeringsblok publiceren hier hun bediening zodra de
 * cursor in hun groep/fase staat. De zijbalk rendert die bediening, zodat de
 * knoppen niet langer op het vel zelf staan. Zo blijft de canvasweergave gelijk
 * aan wat de klant straks in de pdf ziet.
 */

/** Context van de regel waar de cursor in staat. */
export interface InvestmentInspectorRow {
  rateKey?: string;
  showHours?: boolean;
  investmentProduct?: import('@/editor/investmentProduct').InvestmentProduct;
  /** 1-based positie tussen de datarijen. */
  index: number;
  count: number;
  title: string;
  /**
   * Ruwe tekst per cel, in documentvolgorde. Bij uurtarief:
   * [omschrijving, uurtarief, uren, subtotaal]; bij periodiek: [omschrijving, bedrag].
   */
  cells: string[];
  /** Gevuld als deze regel uit de productcatalogus komt. */
  productId: string | null;
  /** Productversie waar de bedragen in deze regel vandaan komen. */
  productVersionId: string | null;
  variantId: string | null;
  /** Bewust gekozen aantal en korting; die maken een regel niet "afwijkend". */
  quantity: number | null;
  discountPct: number | null;
  /** Sales heeft een berekend veld handmatig ingevuld. */
  hasOverrides: boolean;
}

/**
 * Zet een productregel op een andere productversie. Met `cells` worden ook de
 * bedragen overschreven (bijwerken); zonder blijven ze staan (negeren).
 */
export interface InvestmentRowVersionChange {
  productVersionId: string;
  hasOverrides: boolean;
  /** Celindex → nieuwe tekst. */
  cells?: Record<number, string>;
}

/** Groepsniveau: geldt voor het hele investeringsoverzicht. */
export interface InvestmentInspectorGroup {
  groupId: string;
  currency: InvestmentCurrency;
  labelLanguage: InvestmentLabelLanguage;
  setCurrency: (value: string) => void;
  setLabelLanguage: (value: string) => void;
}

/** Faseniveau: geldt voor één tabel binnen het overzicht. */
export interface InvestmentInspectorPhase {
  tableId: string;
  /** Block-id van het investeringsblok; anker voor het zwevende paneel. */
  groupId: string;
  title: string;
  subtitle: string;
  pricingType: PricingType;
  showTotalRow: boolean;
  showHourColumns: boolean;
  /** Uit: alleen het totaal draagt een bedrag, de regels niet. */
  showRowPrices: boolean;
  /** Urenkolommen zijn alleen relevant bij het uurtarief-model. */
  hasHourColumns: boolean;
  /** 0-based positie van de fase, -1 als onbekend. */
  phaseIndex: number;
  phaseCount: number;
  row: InvestmentInspectorRow | null;
  setPricingType: (value: string) => void;
  setShowTotalRow: (value: boolean) => void;
  setShowHourColumns: (value: boolean) => void;
  setShowRowPrices: (value: boolean) => void;
  /** Schrijft één cel van een datarij; rowIndex is 1-based. */
  setRowInvestmentProduct?: (product: import('@/editor/investmentProduct').InvestmentProduct) => void;
  setProductsDisplay?: (display: 'collapsed' | 'expanded') => void;
  setRowCell: (rowIndex: number, cellIndex: number, value: string) => void;
  setRowRate?: (rowIndex: number, rateKey: string, amountCents?: number) => void;
  setRowShowHours?: (rowIndex: number, showHours: boolean) => void;
  /** Zet een datarij op een andere productversie, in één stap (één undo). */
  setRowProductVersion: (rowIndex: number, change: InvestmentRowVersionChange) => void;
  move: (direction: 'up' | 'down') => void;
  duplicate: () => void;
  saveToLibrary: () => void;
  remove: () => void;
}

interface InvestmentInspectorState {
  group: InvestmentInspectorGroup | null;
  phase: InvestmentInspectorPhase | null;
  publishGroup: (group: InvestmentInspectorGroup) => void;
  clearGroup: (groupId: string) => void;
  publishPhase: (phase: InvestmentInspectorPhase) => void;
  clearPhase: (tableId: string) => void;
  /** Telt op bij elk verzoek om het paneel te tonen, ook als het net gesloten is. */
  revealRequest: number;
  requestReveal: () => void;
}

function sameRow(a: InvestmentInspectorRow | null, b: InvestmentInspectorRow | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.investmentProduct === b.investmentProduct &&
    a.index === b.index &&
    a.count === b.count &&
    a.title === b.title &&
    a.productId === b.productId &&
    a.productVersionId === b.productVersionId &&
    a.variantId === b.variantId &&
    a.quantity === b.quantity &&
    a.discountPct === b.discountPct &&
    a.hasOverrides === b.hasOverrides &&
    a.rateKey === b.rateKey &&
    a.showHours === b.showHours &&
    a.cells.length === b.cells.length &&
    a.cells.every((cell, index) => cell === b.cells[index])
  );
}

function sameGroup(
  a: InvestmentInspectorGroup | null,
  b: InvestmentInspectorGroup
): a is InvestmentInspectorGroup {
  return (
    !!a &&
    a.groupId === b.groupId &&
    a.currency === b.currency &&
    a.labelLanguage === b.labelLanguage &&
    a.setCurrency === b.setCurrency &&
    a.setLabelLanguage === b.setLabelLanguage
  );
}

function samePhase(
  a: InvestmentInspectorPhase | null,
  b: InvestmentInspectorPhase
): a is InvestmentInspectorPhase {
  return (
    !!a &&
    a.tableId === b.tableId &&
    a.groupId === b.groupId &&
    a.title === b.title &&
    a.subtitle === b.subtitle &&
    a.pricingType === b.pricingType &&
    a.showTotalRow === b.showTotalRow &&
    a.showHourColumns === b.showHourColumns &&
    a.showRowPrices === b.showRowPrices &&
    a.hasHourColumns === b.hasHourColumns &&
    a.phaseIndex === b.phaseIndex &&
    a.phaseCount === b.phaseCount &&
    sameRow(a.row, b.row) &&
    a.setPricingType === b.setPricingType &&
    a.setShowTotalRow === b.setShowTotalRow &&
    a.setShowHourColumns === b.setShowHourColumns &&
    a.setShowRowPrices === b.setShowRowPrices &&
    a.setRowCell === b.setRowCell &&
    a.setRowRate === b.setRowRate &&
    a.setRowShowHours === b.setRowShowHours &&
    a.setRowProductVersion === b.setRowProductVersion &&
    a.move === b.move &&
    a.duplicate === b.duplicate &&
    a.saveToLibrary === b.saveToLibrary &&
    a.remove === b.remove
  );
}

/**
 * Elke transactie in de editor triggert een publish. De gelijkheidscheck houdt
 * de zijbalk stil zolang er niets verandert — anders rendert hij bij elke
 * toetsaanslag opnieuw.
 */
export const useInvestmentInspectorStore = create<InvestmentInspectorState>()((set) => ({
  group: null,
  phase: null,
  publishGroup: (group) =>
    set((state) => (sameGroup(state.group, group) ? state : { ...state, group })),
  clearGroup: (groupId) =>
    set((state) => (state.group?.groupId === groupId ? { ...state, group: null } : state)),
  publishPhase: (phase) =>
    set((state) => (samePhase(state.phase, phase) ? state : { ...state, phase })),
  clearPhase: (tableId) =>
    set((state) => (state.phase?.tableId === tableId ? { ...state, phase: null } : state)),
  revealRequest: 0,
  requestReveal: () => set((state) => ({ revealRequest: state.revealRequest + 1 })),
}));
